// Extracted upload logic as a pure function for reusability
// Uses callback pattern to handle side effects (logging, error handling, etc.)

export interface S3UploadError {
	code?: string | null;
	message: string | null;
	requestId?: string | null;
	rawXml?: string | null;
}

type BlobObject = {
	id: string;
	buffer: ArrayBuffer;
	conversationId: string;
	sessionClientId: string;
	chunkIndex: number;
};

export type UploadProgressEvent = {
	blobOrFile?: BlobObject | File | Blob;
	completed: boolean;
	loaded: number;
	total: number;
	percentage: number;
	durationSeconds: number;
};

/**
 * Uploads a blob to S3 using a presigned URL
 * @param {BlobObject | File} blobOrFile - The blob object or file object to upload
 * @param {string} presignedUrl - The presigned S3 URL for uploading
 * @param {Function} callback - Callback function to handle upload result
 * @param {any} callback.result - The fetch response (if successful) or error object (if failed)
 * @param {BlobObject | File} callback.blobOrFile - The blob object or file object to upload
 * @param {number} callback.uploadStartTime - Timestamp when upload started
 * @returns {Promise<void>} Promise that resolves when upload completes
 * @example
 * uploadToS3(blob, presignedUrl, (result, blobOrFile, uploadStartTime) => {
 *   if (result.ok) {
 *     console.log('Upload successful');
 *   } else {
 *     console.error('Upload failed:', result.message);
 *   }
 * });
 */
export async function uploadToS3(
	blobOrFile: BlobObject | File | Blob,
	presignedUrl: string,
	callback: (result: Response | S3UploadError, blobOrFile: BlobObject | File | Blob, uploadStartTime: number) => void
) {
	if (!presignedUrl) {
		console.error("Presigned URL is required");
		return;
	}

	if (!blobOrFile) {
		console.error("blobOrFile is required");
		return;
	}
	if (!callback) {
		console.error("Callback function is required");
		return;
	}
	let uploadStartTime = new Date().getTime();

	let result = await fetch(presignedUrl, {
		method: "PUT",
		body: blobOrFile instanceof File || blobOrFile instanceof Blob ? blobOrFile : new Blob([blobOrFile.buffer])
	});

	if (result.ok) {
		callback(result, blobOrFile, uploadStartTime);
	} else {
		const error = await parseS3XmlError(result);
		callback(error, blobOrFile, uploadStartTime);
	}
}

/**
 * Uploads a blob to S3 using a presigned URL with XHR for progress tracking
 * @param {BlobObject | File | Blob} blobOrFile - The blob object or file object to upload
 * @param {string} presignedUrl - The presigned S3 URL for uploading
 * @param {Function} callback - Callback function to handle upload result
 * @param {any} callback.result - The response (if successful) or error object (if failed)
 * @param {BlobObject | File | Blob} callback.blobOrFile - The blob object or file object to upload
 * @param {number} callback.uploadStartTime - Timestamp when upload started
 * @param {Function} progressCallback - Callback function to report upload progress
 * @param {number} progressCallback.loaded - Bytes uploaded so far
 * @param {number} progressCallback.total - Total bytes to upload
 * @param {number} progressCallback.percentage - Upload progress percentage (0-100)
 * @returns {Promise<void>} Promise that resolves when upload completes
 * @example
 * uploadToS3WithProgress(blob, presignedUrl, (result, blobOrFile, uploadStartTime) => {
 *   if (result.ok) {
 *     console.log('Upload successful');
 *   } else {
 *     console.error('Upload failed:', result.message);
 *   }
 * }, (loaded, total, percentage) => {
 *   console.log(`Upload progress: ${percentage}%`);
 * });
 */
export async function uploadToS3WithProgress(
	blobOrFile: BlobObject | File | Blob,
	presignedUrl: string,
	callback: (result: Response | S3UploadError, blobOrFile: BlobObject | File | Blob, uploadStartTime: number) => void,
	progressCallback: (event: UploadProgressEvent) => void
) {
	if (!presignedUrl) {
		console.error("Presigned URL is required");
		return;
	}

	if (!blobOrFile) {
		console.error("blobOrFile is required");
		return;
	}

	if (!callback) {
		console.error("Callback function is required");
		return;
	}

	let uploadStartTime = new Date().getTime();

	const body = blobOrFile instanceof File || blobOrFile instanceof Blob ? blobOrFile : new Blob([blobOrFile.buffer]);

	return new Promise<void>((resolve, reject) => {
		const xhr = new XMLHttpRequest();

		xhr.upload.addEventListener("progress", (event) => {
			if (event.lengthComputable) {
				const loaded = event.loaded;
				const total = event.total;
				const percentage = Math.round((loaded / total) * 100);
				let completed = false;
				let durationSeconds = 0;

				if (percentage == 100) {
					completed = true;
					durationSeconds = (new Date().getTime() - uploadStartTime) / 1000;
				}

				progressCallback({ blobOrFile, completed, loaded, total, percentage, durationSeconds });
			}
		});

		xhr.addEventListener("load", () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				// Create a Response-like object for compatibility
				const response = new Response(null, {
					status: xhr.status,
					statusText: xhr.statusText,
					headers: new Headers()
				});
				// Add ok property
				Object.defineProperty(response, "ok", { value: true, writable: false });
				callback(response, blobOrFile, uploadStartTime);
				resolve();
			} else {
				// Parse error from XML response
				const errorResponse = new Response(xhr.responseText, {
					status: xhr.status,
					statusText: xhr.statusText,
					headers: new Headers()
				});
				parseS3XmlError(errorResponse).then((error) => {
					callback(error, blobOrFile, uploadStartTime);
					resolve();
				});
			}
		});

		xhr.addEventListener("error", () => {
			const error: S3UploadError = {
				code: "NetworkError",
				message: "Network error occurred during upload",
				requestId: null,
				rawXml: null
			};
			callback(error, blobOrFile, uploadStartTime);
			resolve();
		});

		xhr.addEventListener("abort", () => {
			const error: S3UploadError = {
				code: "AbortError",
				message: "Upload was aborted",
				requestId: null,
				rawXml: null
			};
			callback(error, blobOrFile, uploadStartTime);
			resolve();
		});

		xhr.open("PUT", presignedUrl);
		xhr.send(body);
	});
}

/**
 * Parses an S3 XML error response
 * @param {Response} response - The fetch response containing the XML error
 * @returns {Promise<Object>} Object containing the error code, message, request ID, and raw XML (if available)
 */
export async function parseS3XmlError(
	response: Response
): Promise<{ code: string | null; message: string | null; requestId?: string | null; rawXml?: string | null }> {
	const xmlText = await response.text();

	try {
		const parser = new DOMParser();
		const xml = parser.parseFromString(xmlText, "application/xml");

		const get = (tag: string) => xml.querySelector(tag)?.textContent || null;

		return {
			code: get("Code"),
			message: get("Message"),
			requestId: get("RequestId")
		};
	} catch (err) {
		console.error("Failed to parse S3 XML error response", err);
		return {
			code: "XmlParseError",
			message: "Failed to parse S3 XML error response",
			rawXml: xmlText
		};
	}
}
